import { supabase } from '../../lib/supabase';

// ── 손님 후기 쓰기(/review) 공용 — 2026-10-28 대표 터미널 ─────────────────────────
// "정말 꼼꼼하게 진심을 담아서 후기를 써준 사람들은 … 12크레딧" · "사진 첨부도 … 여러 파일도 첨부"
// 서버 규칙(글 길이·첨부 확인·하루 5건·사람당 한 번 지급)은 DB 함수 submit_customer_review /
// admin_decide_customer_review가 다시 지킨다. 여기 값은 화면 안내·미리 검사용이다.

export const REVIEW_REWARD_CREDITS = 12;
export const REVIEW_BUCKET = 'review-attachments';
export const MAX_REVIEW_FILES = 10;
export const MAX_REVIEW_FILE_MB = 50;
export const REVIEW_BODY_MIN = 100;
export const REVIEW_BODY_MAX = 5000;
export const REVIEW_TITLE_MAX = 80;

// 써 본 기능 — ops-journal SERVICES.md '한눈에 보기'의 지금 파는 상품 이름
export const REVIEW_SERVICES = [
    '24시간 방문자 분석',
    '가 계정 분석',
    '방문자 1인 공개',
    '관심 TOP 3',
    '차단·해제 시그널 분석',
    'CEO 직접 검수',
    'VIP 플랜',
    '스토리 뷰어',
    '대화 시뮬레이터',
    '기타',
] as const;

// 버킷 allowed_mime_types와 같은 목록. 아이폰 사진(HEIC)은 브라우저가 형식을 비워 줄 때가 있어 확장자로 채운다.
const EXT_TO_MIME: Record<string, string> = {
    jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', gif: 'image/gif',
    heic: 'image/heic', heif: 'image/heif',
    mp4: 'video/mp4', mov: 'video/quicktime', webm: 'video/webm',
    pdf: 'application/pdf',
};
const ALLOWED_MIME = new Set(Object.values(EXT_TO_MIME));
export const REVIEW_FILE_ACCEPT = 'image/*,video/mp4,video/quicktime,video/webm,application/pdf,.heic,.heif';

const extOf = (name: string): string => (name.includes('.') ? name.split('.').pop() || '' : '').toLowerCase();

export const reviewFileMime = (file: File): string | null => {
    const type = (file.type || '').toLowerCase();
    if (ALLOWED_MIME.has(type)) return type;
    return EXT_TO_MIME[extOf(file.name)] ?? null;
};

export const reviewFileKind = (mime: string | null | undefined): 'image' | 'video' | 'pdf' | 'file' => {
    if (!mime) return 'file';
    if (mime.startsWith('image/')) return 'image';
    if (mime.startsWith('video/')) return 'video';
    if (mime === 'application/pdf') return 'pdf';
    return 'file';
};

// 고른 파일 하나 검사 — 문제가 있으면 손님에게 보일 문장을 돌려준다
export const checkReviewFile = (file: File): string | null => {
    if (!reviewFileMime(file)) return `'${file.name}'은(는) 올릴 수 없는 형식이에요. 사진·영상·PDF만 올릴 수 있어요.`;
    if (file.size > MAX_REVIEW_FILE_MB * 1024 * 1024) return `'${file.name}'이(가) ${MAX_REVIEW_FILE_MB}MB보다 커요.`;
    if (file.size === 0) return `'${file.name}'은(는) 빈 파일이에요.`;
    return null;
};

export const formatFileSize = (bytes: number): string => {
    if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
    return `${Math.max(1, Math.round(bytes / 1024))}KB`;
};

export interface ReviewAttachmentInput {
    path: string;
    name: string;
}

// 본인 폴더(<user_id>/<묶음>/<순번>.<확장자>)에 올린다 — 스토리지 정책이 본인 폴더만 허용
export const uploadReviewFiles = async (
    userId: string,
    files: File[],
    onProgress?: (done: number, total: number) => void,
): Promise<ReviewAttachmentInput[]> => {
    // 옛 아이폰 사파리(15.4 미만)엔 randomUUID가 없다
    const batch = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const out: ReviewAttachmentInput[] = [];
    for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const mime = reviewFileMime(file) as string;
        const ext = extOf(file.name) || mime.split('/')[1];
        const path = `${userId}/${batch}/${i}.${ext}`;
        const { error } = await supabase.storage.from(REVIEW_BUCKET).upload(path, file, { contentType: mime, upsert: false });
        if (error) throw new Error(`파일을 올리지 못했어요 (${i + 1}번째 '${file.name}'). 잠시 뒤 다시 보내 주세요.`);
        out.push({ path, name: file.name });
        onProgress?.(i + 1, files.length);
    }
    return out;
};

const SUBMIT_ERRORS: Record<string, string> = {
    not_authenticated: '로그인이 필요해요.',
    bad_rating: '만족도를 골라 주세요.',
    services_required: '써 본 기능을 하나 이상 골라 주세요.',
    bad_title: `한 줄 요약을 2~${REVIEW_TITLE_MAX}자로 적어 주세요.`,
    body_too_short: `후기를 ${REVIEW_BODY_MIN}자 이상 적어 주세요.`,
    body_too_long: `후기는 ${REVIEW_BODY_MAX.toLocaleString('ko-KR')}자까지 적을 수 있어요.`,
    consent_required: '후기 활용 동의에 체크해 주세요.',
    too_many_files: `파일은 ${MAX_REVIEW_FILES}개까지 올릴 수 있어요.`,
    too_many_reviews: '후기는 하루 5개까지 보낼 수 있어요. 내일 다시 보내 주세요.',
    not_eligible: '실제 사용해 본 체험자만 후기를 쓸 수 있어요.',
    bad_attachment: '파일 올리기가 끝나지 않았어요. 다시 보내 주세요.',
    attachment_missing: '파일 올리기가 끝나지 않았어요. 다시 보내 주세요.',
};

export const reviewSubmitErrorText = (message: string | undefined): string => {
    const code = Object.keys(SUBMIT_ERRORS).find((k) => (message || '').includes(k));
    return code ? SUBMIT_ERRORS[code] : '후기를 보내지 못했어요. 잠시 뒤 다시 보내 주세요.';
};

export type ReviewStatus = 'pending' | 'rewarded' | 'not_rewarded';

export interface MyReviewRow {
    id: string;
    rating: number;
    services: string[];
    title: string;
    body: string;
    attachment_count: number;
    status: ReviewStatus;
    reward_credits: number | null;
    decided_at: string | null;
    created_at: string;
}

export interface MyReviewsResult {
    rewarded: boolean;
    // 대표 10/28 17:59: 입금한 충전(paid)과 크레딧 사용(used)이 둘 다 있어야 후기를 쓸 수 있다(서버 submit도 같은 검사)
    paid: boolean;
    used: boolean;
    can_write: boolean;
    rows: MyReviewRow[];
}

export const fetchMyReviews = async (): Promise<MyReviewsResult> => {
    const { data, error } = await supabase.rpc('my_customer_reviews');
    if (error) throw error;
    return (data as MyReviewsResult | null) ?? { rewarded: false, paid: false, used: false, can_write: false, rows: [] };
};

export const REVIEW_STATUS_BADGE: Record<ReviewStatus, { label: string; cls: string }> = {
    pending: { label: '읽고 있어요', cls: 'bg-white/[0.06] text-[#D4D4D8] border-[#2A2A2F]' },
    rewarded: { label: `${REVIEW_REWARD_CREDITS}크레딧 지급 완료`, cls: 'bg-[rgba(3,178,108,0.15)] text-[#03b26c] border-[rgba(3,178,108,0.3)]' },
    not_rewarded: { label: '이번엔 크레딧 대상이 아니에요', cls: 'bg-[#27272A] text-[#A1A1AA] border-[#3F3F46]' },
};

export const formatReviewDate = (iso: string): string =>
    new Date(iso).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });

// ── 결제 화면에 보여 주는 후기 (대표 10/28 '미결제자가 결제를 고민하는 자리에') ─────────────────────
// 대표가 '손님에게 보여 주기'로 고른 후기 중, 지금까지 입금·기능 사용이 확인된 손님의 글만 서버가 돌려준다(featured_customer_reviews, 10/28 18:4x 기준 변경).
// services = 실제 사용 기록이 있는 기능만, name = 인스타 아이디 앞 두 글자 + ***.
export interface FeaturedReview {
    id: string;
    rating: number;
    title: string;
    body: string;
    services: string[];
    name: string;
    rewarded: boolean;
    created_at: string;
}

export const fetchFeaturedReviews = async (limit = 12): Promise<FeaturedReview[]> => {
    const { data, error } = await supabase.rpc('featured_customer_reviews', { p_limit: limit });
    if (error) throw error;
    return (data as FeaturedReview[] | null) ?? [];
};
